import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { branchFinanceApi } from '../../services/branchFinanceApi';
import {
    BranchLedgerSummary,
    BranchOption,
} from '../../pages/BranchFinances/types/branchFinance.types';

interface BranchFinanceFilters {
    branchId?: number;
    fromDate?: string;
    toDate?: string;
}

interface BranchFinanceState {
    ledger: BranchLedgerSummary | null;
    branches: BranchOption[];
    loading: boolean;
    branchesLoading: boolean;
    error: string | null;
    filters: BranchFinanceFilters;
}

const initialState: BranchFinanceState = {
    ledger: null,
    branches: [],
    loading: false,
    branchesLoading: false,
    error: null,
    filters: {},
};

export const fetchBranchOptions = createAsyncThunk(
    'branchFinance/fetchBranches',
    async () => {
        return await branchFinanceApi.getBranches();
    }
);

export const fetchBranchLedger = createAsyncThunk(
    'branchFinance/fetchLedger',
    async (params: { branchId: number; fromDate?: string; toDate?: string }) => {
        return await branchFinanceApi.getLedger(params);
    }
);

const branchFinanceSlice = createSlice({
    name: 'branchFinance',
    initialState,
    reducers: {
        setFilters: (state, action: PayloadAction<BranchFinanceFilters>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearLedger: (state) => {
            state.ledger = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBranchOptions.pending, (state) => {
                state.branchesLoading = true;
                state.error = null;
            })
            .addCase(fetchBranchOptions.fulfilled, (state, action: PayloadAction<BranchOption[]>) => {
                state.branchesLoading = false;
                state.branches = action.payload;
            })
            .addCase(fetchBranchOptions.rejected, (state, action) => {
                state.branchesLoading = false;
                state.error = action.error.message || 'Failed to load branches';
            })
            .addCase(fetchBranchLedger.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBranchLedger.fulfilled, (state, action) => {
                state.loading = false;
                state.ledger = action.payload.data;
                if (action.payload.meta?.branches) {
                    state.branches = action.payload.meta.branches;
                }
                state.filters = {
                    branchId: action.meta.arg.branchId,
                    fromDate: action.meta.arg.fromDate,
                    toDate: action.meta.arg.toDate,
                };
            })
            .addCase(fetchBranchLedger.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to load ledger';
            });
    },
});

export const { setFilters, clearLedger } = branchFinanceSlice.actions;
export default branchFinanceSlice.reducer;

